import { Badge, Card, Grid, Switch, Text } from '@mantine/core';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import getErrorMessage from '../../helpers/getErrorMessage';
import useFetch from '../../helpers/useFetch';
import { setKlaviyoAccount } from '../../store/klaviyoSlice';
import ErrorMessage from '../ErrorMessage';

// A registered webhook that stops delivering is the failure this row exists to catch, so
// the last delivery time - not the registration flag - decides the colour. 48h matches
// Klaviyo's documented error budget (a webhook that stays in an error state for over 48
// hours is auto-disabled - developers.klaviyo.com, "Working with system webhooks"), and
// the generous window keeps a quiet day at a low-volume sender from crying wolf.
const WEBHOOK_STALE_AFTER_MS = 48 * 60 * 60 * 1000;

// Also returns the delivery line's copy, so the badge and the timestamp below it can never
// disagree - an unparseable value reads as "none received yet" in both, not as the badge
// saying "no delivery received yet" over a literal "Invalid Date".
const realtimeStatus = (klaviyoAccount) => {
  // The sync toggle gates engagement ingestion as a whole - with it off nothing arrives,
  // webhook or not, so neither "Active" nor a 10-minute schedule would be true.
  if (!klaviyoAccount.syncEnabled) {
    return { color: 'gray', label: 'Engagement sync is disabled' };
  }
  if (!klaviyoAccount.webhookEnabled) {
    return { color: 'gray', label: 'Not active - syncing every 10 minutes' };
  }
  const lastReceivedAt = new Date(klaviyoAccount.lastWebhookReceivedAt ?? NaN).getTime();
  if (!Number.isFinite(lastReceivedAt)) {
    return { color: 'yellow', label: 'Active - no delivery received yet', lastDelivery: 'none received yet' };
  }
  const lastDelivery = new Date(lastReceivedAt).toLocaleString();
  if (Date.now() - lastReceivedAt > WEBHOOK_STALE_AFTER_MS) {
    return { color: 'yellow', label: 'Active - no delivery in the last 48h', lastDelivery };
  }
  return { color: 'green', label: 'Active', lastDelivery };
};

// Lives inside the messaging settings' emails tab, whose global Save button only covers
// the domain object - so the Klaviyo toggles save themselves immediately on change.
const KlaviyoSettings = ({ klaviyoAccount }) => {
  const fetch = useFetch();
  const dispatch = useDispatch();
  const { accountId, domainId } = useParams();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState();
  const realtime = realtimeStatus(klaviyoAccount);

  const saveToggle = async (name, value) => {
    setError(undefined);
    setSaving(true);
    try {
      const { klaviyoAccount: savedAccount } = await fetch(
        `/api/admin/accounts/${accountId}/domains/${domainId}/klaviyo/account`,
        {
          method: 'PUT',
          // Partial update - the backend only touches fields present in the body, so a
          // stale client value can never clobber the other toggle.
          body: JSON.stringify({ [name]: value }),
        }
      );
      dispatch(setKlaviyoAccount(savedAccount));
    } catch (error) {
      setError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card withBorder>
      <Card.Section inheritPadding py="sm">
        <Grid>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" fw={500}>
              Status
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Badge color="green" variant="light">
              Connected {klaviyoAccount.authType === 'oauth' ? 'via OAuth' : 'with a private API key'}
            </Badge>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" fw={500}>
              Engagement sync
            </Text>
            <Text size="xs" c="dimmed">
              Pull email engagement events (deliveries, opens, clicks, bounces, spam complaints) and
              subscribe/unsubscribe consent changes from Klaviyo into Releva every 10 minutes.
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Switch
              name="syncEnabled"
              label={klaviyoAccount.syncEnabled ? 'Enabled' : 'Disabled'}
              checked={!!klaviyoAccount.syncEnabled}
              disabled={saving}
              onChange={(event) => saveToggle('syncEnabled', event.currentTarget.checked)}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" fw={500}>
              Include Klaviyo's own campaigns
            </Text>
            <Text size="xs" c="dimmed">
              Also sync engagement from campaigns and flows sent natively in Klaviyo. When off, only emails Releva sends
              through Klaviyo are synced - bounces and consent changes still sync either way.
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Switch
              name="syncNativeEngagement"
              label={klaviyoAccount.syncNativeEngagement ? 'Enabled' : 'Disabled'}
              checked={!!klaviyoAccount.syncNativeEngagement}
              disabled={saving}
              onChange={(event) => saveToggle('syncNativeEngagement', event.currentTarget.checked)}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" fw={500}>
              Last synced
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Text size="sm">
              {klaviyoAccount.lastSyncedAt ? new Date(klaviyoAccount.lastSyncedAt).toLocaleString() : 'Not synced yet'}
            </Text>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" fw={500}>
              Real-time engagement
            </Text>
            <Text size="xs" c="dimmed">
              Set up automatically when the Klaviyo account allows webhooks: engagement is then pushed to Releva within
              minutes instead of the 10-minute schedule.
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Badge color={realtime.color} variant="light">
              {realtime.label}
            </Badge>
            {realtime.lastDelivery && (
              <Text size="sm" mt={4}>
                Last delivery: {realtime.lastDelivery}
              </Text>
            )}
          </Grid.Col>
        </Grid>

        {error && <ErrorMessage message={getErrorMessage(error)} />}
      </Card.Section>
    </Card>
  );
};

export default KlaviyoSettings;
